import axios from 'axios';
import { API_BASE_URL } from '../config/authConfig';
import firebaseInstance from './firebaseSingleton';
import { getAuth, getIdToken } from 'firebase/auth';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 15000,
});

let refreshIntervalId = null;

export const setAuthToken = (token) => {
  if (token) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    localStorage.setItem('authToken', token);
  } else {
    delete api.defaults.headers.common['Authorization'];
    localStorage.removeItem('authToken');
  }
};

export const getAuthToken = () => {
  return localStorage.getItem('authToken');
};

export const removeAuthToken = () => {
  localStorage.removeItem('authToken');
  delete api.defaults.headers.common['Authorization'];
};

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

api.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token && !config.headers['Authorization']) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    const isAuthEndpoint = originalRequest?.url?.includes('/auth/');
    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers['Authorization'] = 'Bearer ' + token;
          return api(originalRequest);
        }).catch(err => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const newAuthToken = await refreshTokenIfNeeded();
        if (newAuthToken) {
          processQueue(null, newAuthToken);
          originalRequest.headers['Authorization'] = 'Bearer ' + newAuthToken;
          return api(originalRequest);
        } else {
          logout();
          const refreshError = new Error('Session expired. Please log in again.');
          processQueue(refreshError, null);
          return Promise.reject(refreshError);
        }
      } catch (e) {
        processQueue(e, null);
        logout();
        return Promise.reject(e);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(handleApiError(error));
  }
);

export const refreshTokenIfNeeded = async () => {
  try {
    if (!firebaseInstance.isInitialized()) {
      console.warn('Firebase not initialized; cannot refresh token.');
      return null;
    }
    const auth = getAuth(firebaseInstance.getApp());
    const user = auth.currentUser;

    if (user) {
      const newToken = await getIdToken(user, true); // Force refresh
      setAuthToken(newToken);
      return newToken;
    }
    
    console.warn('No user is signed in; cannot refresh token.');
    removeAuthToken();
    return null;
  } catch (error) {
    console.error('Failed to refresh Firebase token:', error);
    removeAuthToken();
    window.dispatchEvent(new Event('auth-error'));
    return null;
  }
};

export const startPeriodicRefresh = () => {
  stopPeriodicRefresh();
  refreshIntervalId = setInterval(async () => {
    console.log('Periodically refreshing token...');
    await refreshTokenIfNeeded();
  }, 45 * 60 * 1000); // 45 minutes
};

export const stopPeriodicRefresh = () => {
  if (refreshIntervalId) {
    clearInterval(refreshIntervalId);
    refreshIntervalId = null;
  }
};

export const handleApiError = (error) => {
  if (import.meta.env.DEV) {
    console.error('API Error:', error.response || error.message);
  }

  if (axios.isCancel(error)) {
    return new Error('Request canceled.');
  }

  if (error.response) {
    const message = error.response.data?.error || error.response.data?.message || 'An unexpected server error occurred.';
    const customError = new Error(message);
    customError.status = error.response.status;
    customError.data = error.response.data;
    return customError;
  } else if (error.request) {
    return new Error('Network error. Please check your connection.');
  } else {
    return new Error(error.message || 'An unexpected error occurred.');
  }
};

export const initiateOAuth = async (platform) => {
  try {
    const response = await api.get(`/auth/${platform}`);
    if (response.data.redirectUrl) {
      window.location.href = response.data.redirectUrl;
    } else {
      throw new Error('No redirect URL received from server.');
    }
  } catch (error) {
    console.error(`OAuth initiation for ${platform} failed:`, error);
    throw handleApiError(error);
  }
};

export const getUserProfile = async () => {
  try {
    const response = await api.get('/user/profile');
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

export const updateUserProfile = async (profileData) => {
  try {
    const response = await api.put('/user/profile', profileData);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

export const logout = () => {
  removeAuthToken();
  stopPeriodicRefresh();
  // Firebase sign-out is handled in UserContext to avoid circular dependencies.
  // This just cleans up the API side.
  window.dispatchEvent(new Event('user-logged-out'));
};

export const handleOAuthCallback = async (platform, queryParams) => {
  try {
    const response = await api.get(`/auth/${platform}/callback${queryParams}`);
    if (response.data.token) {
      setAuthToken(response.data.token);
      startPeriodicRefresh();
    }
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

export default api;